import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSpringPageQuery } from '../../../shared/hooks/use-spring-page-query';
import { notificationsApi, type NotificationReportDto } from '../api/notifications.api';
import { useAuthProviderV2 } from '../../../providers/provider-hooks';
import { toast, Slide } from 'react-toastify';
import { delay } from '../../../shared/utils';
import { updateSuccessToast, updateFailureToast } from "../../../toast";


/**
 * Hook for managing sent notifications
 *
 * Automatically filters notifications where current user is the sender.
 * Provides resend functionality and receiver delivery status viewing.
 *
 * @example
 * const { sentNotifications, resendNotification, loading } = useSentNotifications();
 */
export function useSentNotifications() {
    const { userInfo } = useAuthProviderV2();
    const [openedNotification, setOpenedNotification] = useState<string>('');
    const [selectedNotificationId, setSelectedNotificationId] = useState<string | null>(null);

    // Use shared Spring page query hook with sender filter
    const report = useSpringPageQuery<NotificationReportDto, Partial<NotificationReportDto>>({
        queryKey: ['notifications', 'sent', userInfo?.userId || 'unknown'],
        fetchFn: (filter, pageable) => {
            // Always filter by current user as sender
            const sentFilter = {
                ...filter,
                'notification.senderId': userInfo?.userId || ''
            };
            return notificationsApi.getReport(sentFilter, pageable);
        },
        initialFilter: {},
        initialPageable: {
            page: 0,
            size: 20,
            sort: ['notification.sendAt,desc'] // Latest notifications first
        },
        queryOptions: {
            refetchOnMount: 'always', // Always fetch fresh data when tab becomes active
            staleTime: 0, // Consider data immediately stale (always refetch)
        }
    });

    /**
     * Fetch receiver status for a specific notification
     */
    const receiversQuery = useQuery({
        queryKey: ['notifications', 'receivers', selectedNotificationId],
        queryFn: () => selectedNotificationId
            ? notificationsApi.getReceivers(selectedNotificationId)
            : Promise.resolve([]),
        enabled: !!selectedNotificationId,
    });

    /**
     * Read notification content
     */
    const readNotification = useCallback((notificationId: string) => {
        const notification = report.data.find((n) => n.notification?.id === notificationId);
        if (notification?.notification?.content) {
            setOpenedNotification(notification.notification.content);
        }
    }, [report.data]);

    /**
     * Open detailed notification delivery status
     */
    const openDetailedNotificationStatus = useCallback(async (notificationId: string) => {
        const toastId = toast.loading('Detaliile despre aceasta notificare se incarca.', {
            transition: Slide
        });

        try {
            await delay(1000);
            setSelectedNotificationId(notificationId);

            // Wait for query to complete
            const receivers = await notificationsApi.getReceivers(notificationId);

            if (receivers.length === 0) {
                updateFailureToast('Nu exista detalii despre aceasta notificare', toastId);
                setSelectedNotificationId(null);
            } else {
                toast.dismiss(toastId);
            }
        } catch (error: unknown) {
            console.error('Failed to fetch notification receivers:', error);
            updateFailureToast(
                'Detaliile despre aceasta notificare nu s-au putut incarca. Va rugam sa incercati mai tarziu.',
                toastId
            );
            setSelectedNotificationId(null);
        }
    }, []);

    /**
     * Resend notification
     */
    const resendNotification = useCallback(async (notificationId: string) => {
        const toastId = toast.loading('Notificarea se trimite', { transition: Slide });

        try {
            await delay(1000);
            await notificationsApi.resend(notificationId);
            updateSuccessToast('Notificarea a fost retrimisa.', toastId);
        } catch (error: unknown) {
            console.error('Failed to resend notification:', error);
            updateFailureToast(
                'Notificarea nu a putut fi retrimisa. Va rugam sa incercati mai tarziu.',
                toastId
            );
        }
    }, []);

    /**
     * Close notification details dialog
     */
    const closeNotificationDetails = useCallback(() => {
        setSelectedNotificationId(null);
    }, []);

    return {
        // Data
        sentNotifications: report.data,
        notificationDetails: receiversQuery.data || [],

        // Loading states
        loading: report.loading,
        isLoading: report.isLoading,
        detailsLoading: receiversQuery.isLoading,

        // Pagination
        pagination: {
            currentPage: report.currentPage,
            totalPages: report.totalPages,
            totalElements: report.totalElements,
            pageSize: report.pageSize,
            first: report.currentPage === 0,
            last: report.currentPage === report.totalPages - 1,
        },
        goToPage: report.goToPage,
        changePageSize: report.changePageSize,

        // Actions
        readNotification,
        resendNotification,
        openDetailedNotificationStatus,
        closeNotificationDetails,
        refetch: report.refetch,

        // Dialog states
        openedNotification,
        setOpenedNotification,
    };
}
